import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CreditCard, Info } from 'lucide-react';
import type { MeioCobranca, SituacaoAssinatura, SituacaoCiclo } from '@social/shared';
import { PaginaShell } from '../componentes/pagina-shell';
import { Botao, Etiqueta } from '../componentes/ui';
import { Spinner } from '../componentes/carregando';
import { comEspera } from '../lib/ocupado';
import { ErroApi } from '../lib/api';
import { api, useSessao } from '../lib/sessao';
import { moeda } from './sociedade-comum';
import { BlocoDePagamento } from './assinatura-pagamento';
import {
  avisoDeEstado,
  linhasDeUso,
  ROTULO_CICLO,
  ROTULO_PERIODICIDADE,
  ROTULO_SITUACAO,
  tomDaSituacao,
  type LinhaDeUso,
  type Panorama,
  type PlanoResumo,
} from './assinatura-comum';

/*
  A assinatura do escritório.

  ## As seis perguntas (skill `social-desenho`)
  1. **Qual pergunta a pessoa traz?** "Até quando posso usar isto, e quanto já
     estou usando do que contratei?" As duas metades importam: a data sozinha
     não diz se o plano cabe, e o uso sozinho não diz até quando.
  2. **Quantos registros na vida real?** Uma assinatura, um plano, três planos
     em catálogo, e um ciclo por período — doze por ano no mensal. É escala de
     DEZENAS: sem busca, sem paginação, sem KPI. Montar aparato de consulta
     aqui faria a tela parecer inacabada, não sofisticada.
  3. **Resultado vs aparência?** Não há filtro nenhum — nada nesta tela muda o
     que ela mostra. Por isso não há barra de ferramentas.
  4. **Barra ou gaveta?** Não se aplica.
  5. **375px?** Tudo empilha numa coluna; os ciclos viram lista de linhas com
     rótulo, e não uma tabela espremida.
  6. **O que promete e não existe?** É AQUI que esta tela quase mentiu.
     **Não existe gateway de pagamento** (§9 da spec: o ciclo nasce
     `PENDENTE` e alguém confirma à mão). Um botão "Assinar agora" abriria a
     expectativa de um checkout que não vem, e o escritório ficaria esperando
     uma cobrança que ninguém emitiu. O botão diz **"Escolher este plano"**, e
     o texto ao lado diz que a cobrança é combinada com a Specializer — que é
     o que de fato acontece.
*/

export function Assinatura(): JSX.Element {
  const fila = useQueryClient();
  const papel = useSessao((s) => s.papel);
  const ehAdministrador = papel === 'administrador';

  const panorama = useQuery({
    queryKey: ['assinatura'],
    queryFn: () => api.get<Panorama>('/assinatura'),
  });

  const trocar = useMutation({
    mutationFn: (planoCodigo: string) =>
      comEspera(
        () => api.post<Panorama>('/assinatura/plano', { planoCodigo }),
        'Trocando o plano',
      ),
    onSuccess: (novo) => {
      // Escreve a resposta direto na fila em vez de invalidar: o servidor já
      // devolveu o panorama inteiro e novo, e uma segunda ida mostraria o
      // estado velho no intervalo.
      fila.setQueryData(['assinatura'], novo);
      // A troca mexe no limite, e o limite muda o que a lista de sociedades
      // pode fazer — a guarda de escrita lê a assinatura.
      void fila.invalidateQueries({ queryKey: ['sociedades'] });
    },
  });

  const cobrar = useMutation({
    mutationFn: (meio: MeioCobranca) =>
      comEspera(
        () => api.post<unknown>('/assinatura/cobranca', { meio }),
        'Gerando a cobrança',
      ),
    // Invalida em vez de escrever a resposta: a rota devolve a COBRANÇA, não o
    // panorama, e a tela precisa do panorama inteiro (o ciclo mudou junto).
    onSuccess: () => fila.invalidateQueries({ queryKey: ['assinatura'] }),
  });

  const faturar = useMutation({
    mutationFn: (dados: { cnpj: string; emailCobranca: string }) =>
      comEspera(
        () => api.post<Panorama>('/assinatura/faturamento', dados),
        'Salvando os dados',
      ),
    onSuccess: (novo) => fila.setQueryData(['assinatura'], novo),
  });

  const p = panorama.data;
  const aviso = p ? avisoDeEstado(p.acesso) : null;
  /*
    O período em aberto: só o mais recente, e só quando há valor a pagar.
    Ciclo isento (trial, cortesia) não gera cobrança nenhuma — o bloco some.
  */
  const cicloEmAberto = p?.ciclos.find(
    (c) => c.situacao === 'PENDENTE' || c.situacao === 'VENCIDO',
  );

  return (
    <PaginaShell
      titulo="Assinatura"
      subtitulo="O plano do escritório, o que ele permite e o que já está em uso."
    >
      {panorama.isPending && (
        <p className="text-tinta-500 flex items-center gap-2 text-sm">
          <Spinner /> Carregando a assinatura…
        </p>
      )}

      {panorama.isError && (
        <p
          role="alert"
          className="bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm"
        >
          Não foi possível ler a assinatura. Atualize a página.
        </p>
      )}

      {p && (
        <div className="flex flex-col gap-6">
          {aviso && (
            <p
              /*
                Nome acessível próprio porque o `Spinner` também é
                `role="status"`: sem ele, leitor de tela e teste veem duas
                regiões de status sem saber qual é qual — e a que carrega a
                notícia é esta.
              */
              role="status"
              aria-label="Estado da assinatura"
              className={
                aviso.tom === 'erro'
                  ? 'bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm'
                  : 'bg-alerta-100 text-alerta-600 rounded-lg px-4 py-3 text-sm'
              }
            >
              {aviso.texto}
            </p>
          )}

          <PlanoAtual panorama={p} />
          <Uso linhas={linhasDeUso(p.uso, p.limites)} />
          <Planos
            disponiveis={p.planosDisponiveis}
            atual={p.plano?.codigo ?? null}
            situacao={p.assinatura?.situacao ?? null}
            podeTrocar={ehAdministrador}
            trocando={trocar.isPending ? trocar.variables : null}
            erro={trocar.isError}
            aoEscolher={(codigo) => trocar.mutate(codigo)}
          />
          {cicloEmAberto && (
            <BlocoDePagamento
              cobranca={p.cobrancaEmAberto}
              valorCentavos={cicloEmAberto.valorCentavos}
              disponivel={p.pagamentoDisponivel}
              faturamentoCompleto={p.faturamentoCompleto}
              faturamento={p.faturamento}
              podeCobrar={ehAdministrador}
              gerando={cobrar.isPending ? cobrar.variables : null}
              salvandoFaturamento={faturar.isPending}
              erro={
                cobrar.isError || faturar.isError
                  ? mensagemDoErro(cobrar.error ?? faturar.error)
                  : null
              }
              aoGerar={(meio) => cobrar.mutate(meio)}
              aoSalvarFaturamento={(dados) => faturar.mutate(dados)}
            />
          )}

          {p.ciclos.length > 0 && <Ciclos ciclos={p.ciclos} />}
        </div>
      )}
    </PaginaShell>
  );
}

function Bloco({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-tinta-900 text-base font-semibold">{titulo}</h2>
      {children}
    </section>
  );
}

function PlanoAtual({ panorama }: { panorama: Panorama }): JSX.Element {
  const { assinatura, plano } = panorama;

  if (!assinatura) {
    return (
      <Bloco titulo="Plano atual">
        <p className="text-tinta-500 text-sm">
          Este escritório ainda não tem plano. Escolha um abaixo para começar.
        </p>
      </Bloco>
    );
  }

  return (
    <Bloco titulo="Plano atual">
      <div className="border-tinta-100 bg-campo flex flex-wrap items-center gap-x-8 gap-y-3 rounded-xl border p-4">
        <Dado rotulo="Plano">{plano?.nome ?? '—'}</Dado>
        <Dado rotulo="Situação">
          <Etiqueta tom={tomDaSituacao(assinatura.situacao)}>
            {ROTULO_SITUACAO[assinatura.situacao]}
          </Etiqueta>
        </Dado>
        <Dado rotulo="Vigência">
          {dataCurta(assinatura.inicio)} a {dataCurta(assinatura.fim)}
        </Dado>
        <Dado rotulo="Valor">
          {/*
            O preço CONTRATADO, não o do catálogo: quem fechou por um valor
            continua pagando aquele valor, e mostrar o preço de tabela faria a
            tela contradizer a fatura.
          */}
          {assinatura.precoContratadoCentavos === 0
            ? 'Sem cobrança'
            : moeda(assinatura.precoContratadoCentavos)}
        </Dado>
      </div>
    </Bloco>
  );
}

function Dado({
  rotulo,
  children,
}: {
  rotulo: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-tinta-500 text-xs">{rotulo}</dt>
      <dd className="text-tinta-900 text-sm font-medium">{children}</dd>
    </div>
  );
}

function Uso({ linhas }: { linhas: LinhaDeUso[] }): JSX.Element {
  return (
    <Bloco titulo="Uso">
      <ul className="flex flex-col gap-4">
        {linhas.map((l) => (
          <li key={l.rotulo} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-tinta-700 text-sm">{l.rotulo}</span>
              <span className="text-tinta-900 text-sm font-medium tabular-nums">
                {l.limite === null ? (
                  <>
                    {l.usado}{' '}
                    <span className="text-tinta-500 font-normal">sem limite</span>
                  </>
                ) : (
                  `${String(l.usado)} de ${String(l.limite)}`
                )}
              </span>
            </div>
            {/*
              Sem limite, sem barra: uma barra precisa de um fim para significar
              alguma coisa, e desenhá-la vazia sugeriria que há teto em algum
              lugar fora da tela.
            */}
            {l.percentual !== null && (
              <div
                className="bg-tinta-100 h-1.5 overflow-hidden rounded-full"
                role="progressbar"
                aria-label={l.rotulo}
                aria-valuenow={l.percentual}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={
                    l.estourado
                      ? 'bg-erro-600 h-full rounded-full'
                      : l.perto
                        ? 'bg-alerta-600 h-full rounded-full'
                        : 'bg-marca-600 h-full rounded-full'
                  }
                  style={{ width: `${String(l.percentual)}%` }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </Bloco>
  );
}

function Planos({
  disponiveis,
  atual,
  situacao,
  podeTrocar,
  trocando,
  erro,
  aoEscolher,
}: {
  disponiveis: PlanoResumo[];
  atual: string | null;
  situacao: SituacaoAssinatura | null;
  podeTrocar: boolean;
  trocando: string | null;
  erro: boolean;
  aoEscolher: (codigo: string) => void;
}): JSX.Element {
  return (
    <Bloco titulo="Planos">
      {/*
        A frase que impede a promessa falsa. Ver a pergunta 6 no topo do
        arquivo: não há checkout, e dizer isso aqui custa uma linha — descobrir
        sozinho custa uma semana esperando um boleto que não vem.
      */}
      <p className="text-tinta-500 flex items-start gap-2 text-sm">
        <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span>
          A escolha registra o plano e libera o acesso na hora. A cobrança é combinada com
          a Specializer — não há pagamento por aqui.
        </span>
      </p>

      {erro && (
        <p
          role="alert"
          className="bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm"
        >
          Não foi possível trocar o plano. Tente de novo.
        </p>
      )}

      <ul className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {disponiveis.map((plano) => {
          const ehAtual = plano.codigo === atual && situacao !== 'CANCELADA';
          return (
            <li
              key={plano.codigo}
              className={
                ehAtual
                  ? 'border-marca-600 bg-campo flex flex-col gap-3 rounded-xl border-2 p-4'
                  : 'border-tinta-100 bg-campo flex flex-col gap-3 rounded-xl border p-4'
              }
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-tinta-900 font-semibold">
                  {plano.nome}{' '}
                  <span className="text-tinta-500 font-normal">
                    {ROTULO_PERIODICIDADE[plano.periodicidade]}
                  </span>
                </h3>
                {ehAtual && (
                  <Etiqueta tom="ok">
                    <Check aria-hidden className="mr-1 size-3" />
                    Atual
                  </Etiqueta>
                )}
              </div>

              <p className="text-tinta-900 text-lg font-bold">
                {moeda(plano.precoCentavos)}
              </p>

              <ul className="text-tinta-500 flex flex-col gap-1 text-sm">
                <li>{limiteEmPalavras(plano.limites.sociedades, 'sociedade')}</li>
                <li>{limiteEmPalavras(plano.limites.usuarios, 'usuário')}</li>
                <li>{limiteEmPalavras(plano.limites.atosPorMes, 'ato por mês', true)}</li>
              </ul>

              <div className="mt-auto pt-1">
                {ehAtual ? (
                  <p className="text-tinta-500 text-sm">Este é o seu plano.</p>
                ) : (
                  <Botao
                    variante="secundaria"
                    disabled={!podeTrocar || trocando !== null}
                    title={
                      podeTrocar
                        ? undefined
                        : 'Só o administrador do escritório pode mudar de plano.'
                    }
                    onClick={() => {
                      aoEscolher(plano.codigo);
                    }}
                  >
                    {trocando === plano.codigo ? (
                      <>
                        <Spinner /> Trocando…
                      </>
                    ) : (
                      <>
                        <CreditCard aria-hidden className="size-4" />
                        Escolher este plano
                      </>
                    )}
                  </Botao>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Bloco>
  );
}

/**
 * "10 sociedades" ou "Sociedades sem limite" — nunca "null" nem "0".
 *
 * O plural do português não é o do inglês em "1 ato por mês": o `sufixoPlural`
 * existe porque "ato por mês" pluraliza no meio da frase ("atos por mês"), e
 * concatenar um "s" no fim produziria "ato por mêss".
 */
function limiteEmPalavras(limite: number | null, singular: string, meio = false): string {
  const plural = meio ? singular.replace(' por ', 's por ') : `${singular}s`;
  if (limite === null) {
    return `${maiuscula(plural)} sem limite`;
  }
  return `${String(limite)} ${limite === 1 ? singular : plural}`;
}

function maiuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function Ciclos({ ciclos }: { ciclos: Panorama['ciclos'] }): JSX.Element {
  return (
    <Bloco titulo="Períodos">
      {/*
        Lista, não `Tabela`: são quatro colunas curtas e poucas linhas, e o
        componente de tabela traz ordenação, colunas e paginação — aparato que
        aqui só ocuparia altura. Em 375px a linha quebra sozinha.
      */}
      <ul className="divide-tinta-100 border-tinta-100 divide-y rounded-xl border">
        {ciclos.map((c) => (
          <li
            key={c.sequencia}
            className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 py-3"
          >
            <span className="text-tinta-900 text-sm">
              {dataCurta(c.inicio)} a {dataCurta(c.fim)}
            </span>
            <span className="text-tinta-900 text-sm font-medium tabular-nums">
              {c.valorCentavos === 0 ? 'Sem cobrança' : moeda(c.valorCentavos)}
            </span>
            <Etiqueta tom={tomDoCiclo(c.situacao)}>{ROTULO_CICLO[c.situacao]}</Etiqueta>
          </li>
        ))}
      </ul>
    </Bloco>
  );
}

/**
 * Vencido é ALERTA, não neutro: é o único estado de ciclo em que o escritório
 * precisa fazer alguma coisa, e pintá-lo igual a "a acertar" esconderia
 * justamente a linha que pede ação.
 */
function tomDoCiclo(s: SituacaoCiclo): 'neutro' | 'ok' | 'alerta' {
  if (s === 'PAGO' || s === 'ISENTO') return 'ok';
  if (s === 'VENCIDO') return 'alerta';
  return 'neutro';
}

/** `2026-08-30` → `30/08/2026`, por fatia — ver `primeiroDiaDoMes` na API. */
function dataCurta(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia ?? ''}/${mes ?? ''}/${ano ?? ''}`;
}

/** A mensagem do servidor é melhor que qualquer texto genérico daqui. */
function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message;
  return 'Não foi possível concluir. Tente de novo.';
}
